import { z } from 'zod';
import { profileSchema } from './profile.js';
import { UNIT_OPTIONS } from '../constants.js';

export const mealPlanRequestSchema = z.object({
  weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'weekStart must be YYYY-MM-DD'),
  preferences: profileSchema.optional().nullable(),
  // Phase B: per-generation override. When true, every meal must be ≤20 min
  // even if the user's profile has busyNightMode off. Profile setting still
  // wins when this is omitted/false.
  busyWeek: z.boolean().optional(),
}).strict();

// Schema for Claude's response — strict to reject unexpected fields
const mealIngredientSchema = z.object({
  ingredientId: z.string().uuid(),
  name: z.string().min(1).max(200),
  quantity: z.number().positive(),
  unit: z.string().max(50),
}).strict();

const sideDishSchema = z.object({
  name: z.string().min(1).max(200),
  recipeId: z.string().uuid().optional(),
  ingredients: z.array(mealIngredientSchema).min(1).max(20),
  instructions: z.array(z.string().min(1).max(1000)).min(1).max(10),
  cookTimeMins: z.number().int().positive().optional(),
}).strict();

const mealSchema = z.object({
  type: z.enum(['breakfast', 'lunch', 'dinner']),
  name: z.string().min(1).max(200),
  ingredients: z.array(mealIngredientSchema).min(1).max(50),
  instructions: z.array(z.string().min(1).max(1000)).min(1).max(20),
  // Engagement fields — added at generation time from recipe_library (never from Claude)
  cookTimeMins: z.number().int().positive().optional(),
  difficulty: z.enum(['easy', 'intermediate', 'expert']).optional(),
  // Phase B: cooking method tags — populated from recipes.cooking_method.
  // Vocabulary matches user_profiles.equipment so set-overlap is direct.
  cookingMethod: z.array(z.string().max(40)).max(8).optional(),
  tagline: z.string().max(200).optional(),
  imageUrl: z.string().optional(),
  // Phase C: recipe DB ID for favorites
  recipeId: z.string().uuid().optional(),
  // Nutritional overhaul: side dishes alongside main (dinner/lunch only, max 2)
  sides: z.array(sideDishSchema).max(2).optional(),
}).strict();

const daySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
  meals: z.array(mealSchema).min(1).max(5),
}).strict();

// G2 Area 3 — budget swap log entry
export const swapLogEntrySchema = z.object({
  originalRecipeId: z.string().uuid(),
  originalRecipeName: z.string().max(200).optional(),
  replacementRecipeId: z.string().uuid(),
  reason: z.enum(['budget_swap']),
}).strict();

export const mealPlanResponseSchema = z.object({
  days: z.array(daySchema).min(7).max(7),
  // G2 Area 3 — record of meals auto-swapped to fit budget
  swapLog: z.array(swapLogEntrySchema).optional(),
  // G2 Area 3 — surfaced when budget can't be met
  estimatedMinimumWeeklyBudget: z.number().nullable().optional(),
}).strict();

// Phase I: Lock state — which meals are pinned by the user
export const lockedMealSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
  mealType: z.enum(['breakfast', 'lunch', 'dinner']),
});

export const updateLockedMealsSchema = z.object({
  lockedMeals: z.array(lockedMealSchema).max(21), // max 7 days × 3 meals
}).strict();

export const regenerateDayRequestSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
}).strict();

export type MealPlanRequest = z.infer<typeof mealPlanRequestSchema>;
export type MealPlanResponse = z.infer<typeof mealPlanResponseSchema>;
export type MealDay = z.infer<typeof daySchema>;
export type Meal = z.infer<typeof mealSchema>;
export type MealIngredient = z.infer<typeof mealIngredientSchema>;
export type SideDish = z.infer<typeof sideDishSchema>;
export type LockedMeal = z.infer<typeof lockedMealSchema>;
export type SwapLogEntry = z.infer<typeof swapLogEntrySchema>;
